/**
 * Block 13 «Договор на мебель» — Zod-контракты.
 *
 * Defines:
 *   - `ContractTemplateSchema` — структура production JSON в
 *     `apps/web/src/features/contract/templates/{key}.json` (5 templates).
 *     Источник — `scripts/extract-contract-templates.py` (one-time экстракция
 *     из 5 .docx источников). P-029 spirit: legal text верстается verbatim
 *     из .docx; meta + section IDs дополнены CC.
 *   - `ContractDraftSchema` — состояние формы 2.13.2 (в IDB).
 *     contains transient PII (customerRequisites, P-009) — clearable
 *     post-PDF generation (Phase 7).
 *   - `ContractSpecBlockSchema` / `ContractSpecRowSchema` — Приложение №1
 *     Спецификация: блоки мебели + позиции с переключателем источника
 *     («Из списка» / «Вручную» / «Из КП»).
 *   - `ContractRequisiteSchema` — одна строка анкеты Реквизитов
 *     (Заказчик/Исполнитель), 7 базовых полей KZ + custom.
 *
 * Этот контракт ВВОДИТСЯ через ADR-0025 (P-011 — контракты заморожены, новые
 * требуют ADR). Изменение полей этого файла после Phase 1 — отдельный approve.
 *
 * **Transient PII (P-009).** `ContractDraft.customerRequisites[].value` хранит
 * чувствительные данные клиента (ИИН, БИН, паспорт, адрес, телефон). После
 * успешной генерации PDF клиент вызывает `clearTransientRequisites(contractId)`
 * (Phase 7) — обнуляет `.value` строки оставляя `label`/`key`/`removable` для
 * "договор сформирован" history. На сервер ПДн **никогда** не уходят
 * (Block 13 frontend-only до Phase 9+).
 *
 * References:
 *   - ADR-0025 «Block 13 contract architecture»
 *   - PRD `docs/prd/v4/11-block-13-contract.md` Экраны 2.13.1–2.13.3
 *   - Reference pattern: `contracts/quote.ts`, `contracts/calculator.ts`
 */

import { z } from 'zod';
import {
  CONTRACT_REGIONS,
  CONTRACT_LANGUAGES,
  CONTRACT_TEMPLATE_KEYS,
  CONTRACT_CURRENCY_CODES,
} from '../domain/contract-regions-vocabulary';
import { QUOTE_FURNITURE_TYPES } from '../domain/quote-furniture-types-vocabulary';

// ── Template (read-only, loaded from JSON) ────────────────────────────────

/** Stable section IDs — Phase 2 PDF rendering substitutes dynamic data per ID. */
export const CONTRACT_SECTION_IDS = [
  'subject',
  'cost',
  'rights_obligations',
  'terms',
  'liability',
  'force_majeure',
  'warranty',
  'term_final',
  'requisites',
] as const;
export type ContractSectionId = (typeof CONTRACT_SECTION_IDS)[number];
export const ContractSectionIdSchema = z.enum(CONTRACT_SECTION_IDS);

export const ContractTemplateMetaSchema = z.object({
  region: z.enum(CONTRACT_REGIONS),
  lang: z.enum(CONTRACT_LANGUAGES),
  languageLabel: z.string().min(1).max(50),
  currency: z.string().min(1).max(20),
  currencyShort: z.string().min(1).max(10),
  currencyCode: z.enum(CONTRACT_CURRENCY_CODES),
  defaultCity: z.string().min(1).max(100),
});
export type ContractTemplateMeta = z.infer<typeof ContractTemplateMetaSchema>;

export const ContractTemplateSectionSchema = z.object({
  id: ContractSectionIdSchema,
  number: z.string().min(1).max(3),
  heading: z.string().min(1).max(500),
  paragraphs: z.array(z.string().min(1).max(5000)),
});
export type ContractTemplateSection = z.infer<typeof ContractTemplateSectionSchema>;

export const ContractTemplateRequisitesSchema = z.object({
  /** 2D string array — strings extracted from .docx requisites table verbatim. */
  rows: z.array(z.array(z.string().max(500))),
});

export const ContractTemplateSpecSchema = z.object({
  appendixParagraphs: z.array(z.string().min(1).max(500)),
  columns: z.array(z.string().min(1).max(200)),
  defaultRows: z.array(z.array(z.string().max(500))),
});

export const ContractTemplateSchema = z.object({
  meta: ContractTemplateMetaSchema,
  title: z.string().min(1).max(500),
  dateCityLine: z.string().min(1).max(500),
  preamble: z.string().min(1).max(5000),
  sections: z.array(ContractTemplateSectionSchema).length(9),
  requisites: ContractTemplateRequisitesSchema,
  spec: ContractTemplateSpecSchema,
  signatureFooter: z.string().min(1).max(500).nullable(),
});
export type ContractTemplate = z.infer<typeof ContractTemplateSchema>;

// ── Draft (mutable, in IDB) ────────────────────────────────────────────────

/**
 * Phase 6.5F (ADR-0025 Amendment A7, 2026-05-25) — Spec schema rewrite:
 * Тип/Бренд отдельные поля, drop `value`, drop 3 material slots.
 *
 * **Before (post-6.5B A2):** 4 fixed rows (facade_material/corpus_material/
 * hardware/block_cost) + custom rows; combined `value: string` field
 * («МДФ Egger»).
 *
 * **After (6.5F A7):**
 *   - Single fixed key `block_cost` (label «Стоимость», cost numeric only).
 *   - Custom rows (`key='custom-<uuid>'`, `removable: true`) — user-defined
 *     label («Столешница», «Фасады», «Фурнитура») + `type` + `brand` separate
 *     optional fields (mirror КП CompositionRow {name, itemType, brand}).
 *
 * **Rationale (Aibek visual verify round 3, 2026-05-24):** combined `value`
 * field теряло Тип/Бренд discrimination при mapping от КП → contract
 * (`buildValue(itemType, brand)` = lossy concat). New schema 1:1 matches КП
 * structure, mapping становится тривиальным direct copy.
 */
export const CONTRACT_SPEC_FIXED_ROW_KEYS = ['block_cost'] as const;
export type ContractSpecFixedRowKey = (typeof CONTRACT_SPEC_FIXED_ROW_KEYS)[number];

/** Один позиционный элемент Спецификации (внутри блока мебели). */
export const ContractSpecRowSchema = z.object({
  id: z.string().uuid(),
  /**
   * Stable key. `block_cost` — fixed cost row. Custom rows — `custom-<uuid>`
   * (mirror customerRequisites pattern в `contractReducer.ts`).
   */
  key: z.string().min(1).max(100),
  /** Display label. block_cost — locked «Стоимость». Custom rows — user-editable. */
  label: z.string().max(200).default(''),
  /**
   * Phase 6.5F A7 — separate Тип field (e.g. «ЛДСП», «ПВХ», «Пленка»).
   * Optional: только custom rows имеют type. block_cost — undefined.
   */
  type: z.string().max(500).optional(),
  /**
   * Phase 6.5F A7 — separate Бренд field (e.g. «Эггер», «Кира», «Blum»).
   * Optional: только custom rows имеют brand. block_cost — undefined.
   */
  brand: z.string().max(500).optional(),
  /**
   * Стоимость позиции (валюта по `ContractDraft.currencyCode`).
   * **Optional:** для block_cost — defined (initial 0). Для custom rows —
   * user может оставить undefined (если позиция в общей сумме без отдельной
   * цены). `selectTotalSpec` использует `row.cost ?? 0`. Universal cap
   * 100B currency units (см. B-146 + B-150 в BACKLOG).
   */
  cost: z.number().int().min(0).max(100_000_000_000).optional(),
  /** False = fixed block_cost row (label locked). True = user-added custom row. */
  removable: z.boolean().default(false),
});
export type ContractSpecRow = z.infer<typeof ContractSpecRowSchema>;

/** Блок мебели — title (тип мебели, free text) + список rows (4 fixed + custom). */
export const ContractSpecBlockSchema = z.object({
  id: z.string().uuid(),
  /** Тип мебели — free text (e.g. «Кухня», «Шкаф»). */
  title: z.string().min(1).max(200),
  rows: z.array(ContractSpecRowSchema).max(100),
  /**
   * PR-C5 (ADR-0039, 2026-05-31) — UUID КП-источника блока. Проставляется при
   * первом севе из КП (`mapQuoteStateToContractDraft` → `spec[0]`). Адресует
   * «родной» блок при подтверждаемом пересеве (`PARTIAL_RESEED_CONTRACT` ищет
   * блок `sourceQuoteId === fromQuoteId`). Блоки из «+ Добавить мебель» — без
   * маркера (или с чужим id) → не пересеваются. Optional: legacy-договоры без
   * маркера → fallback на первый блок с `block_cost`. IDB version НЕ бампится.
   */
  sourceQuoteId: z.string().uuid().optional(),
});
export type ContractSpecBlock = z.infer<typeof ContractSpecBlockSchema>;

/**
 * Phase 6.5 (ADR-0025 Amendment A3) — custom rows в SectionCostAndTerms (#7).
 *
 * Свободные строки в Cost-секции (например «Срок гарантии (нестандартный)»,
 * «Особые условия по доставке»). All rows are user-added → always removable
 * → `removable` flag dropped (dead weight per Aibek decision 2026-05-24).
 *
 * PDF render integration ships в Phase 6.5C (renderContractToPdf cost section).
 */
export const ContractCostCustomRowSchema = z.object({
  id: z.string().uuid(),
  // Phase 6.5B Amendment A3 — label / value start пустыми (user заполняет через
  // UI после «+ Добавить строку»). Non-empty enforcement при PDF render
  // в 6.5C — empty rows скипаются renderer'ом (UX trap protection).
  label: z.string().max(200).default(''),
  value: z.string().max(500).default(''),
});
export type ContractCostCustomRow = z.infer<typeof ContractCostCustomRowSchema>;

/** Известные ключи требизитов KZ (минимальный set). RU/UZ расширяют через custom rows. */
export const CONTRACT_KNOWN_REQUISITE_KEYS = [
  'name',
  'address',
  'iin_bin',
  'phone',
  'iik',
  'bank',
  'bik',
  // RU-specific (опц.)
  'inn',
  'kpp',
  'ogrn',
  'ogrnip',
  'r_s',
  'k_s',
  // UZ-specific (опц.)
  'pinfl',
  'mfo',
] as const;
export type ContractKnownRequisiteKey = (typeof CONTRACT_KNOWN_REQUISITE_KEYS)[number];

/**
 * Один элемент анкеты Реквизитов — Заказчик или Исполнитель.
 * `removable: false` — fixed field (label не редактируемый, value transient).
 * `removable: true` — user-added custom row (label + value редактируемые).
 *
 * P-009 transient PII flow: `customerRequisites[].value` clearable post-PDF;
 * `executorRequisites[].value` обычно сохраняется (это собственные реквизиты
 * пользователя приложения, не клиента).
 */
export const ContractRequisiteSchema = z.object({
  id: z.string().min(1).max(200),
  /** Stable key для default rows; custom rows используют 'custom-{uuid}'. */
  key: z.string().min(1).max(100),
  label: z.string().min(1).max(200),
  value: z.string().max(500).default(''),
  removable: z.boolean().default(false),
});
export type ContractRequisite = z.infer<typeof ContractRequisiteSchema>;

/** Источник стоимости из формы 2.13.2: «Из КП / Вручную» chip-segment. */
export const CONTRACT_COST_SOURCES = ['fromQuote', 'manual'] as const;
export type ContractCostSource = (typeof CONTRACT_COST_SOURCES)[number];

/** Гарантия — единицы (chip-segment per PRD). */
export const CONTRACT_WARRANTY_UNITS = ['months', 'years'] as const;
export type ContractWarrantyUnit = (typeof CONTRACT_WARRANTY_UNITS)[number];

/** Жизненный цикл договора per PRD §10.13.1 «Мои договоры». */
export const CONTRACT_STATUSES = ['draft', 'ready', 'sent'] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

/**
 * Полная схема Draft-а договора — состояние формы 2.13.2 + persistence в IDB.
 *
 * Большинство полей с `.default()` чтобы автосейв во время заполнения
 * (Phase 4 debounce 500ms) сохранял частично заполненную форму. Validation
 * "ready to render PDF" — отдельный refined schema будет в Phase 2.
 *
 * `customerRequisites[].value` MUST содержать PII (ИИН/БИН/паспорт/адрес/телефон)
 * только пока пользователь работает над draft. Phase 7 реализует
 * `clearTransientRequisites(contractId)` — клиренсит values после PDF gen.
 */
export const ContractDraftSchema = z.object({
  id: z.string().uuid(),
  templateKey: z.enum(CONTRACT_TEMPLATE_KEYS),
  status: z.enum(CONTRACT_STATUSES).default('draft'),

  // Преамбула
  contractNum: z.string().max(50).default(''),
  /** ISO date YYYY-MM-DD — frontend formats per locale при render. */
  contractDate: z.string().max(50).default(''),
  contractCity: z.string().max(200).default(''),

  // Стороны (одно поле name + опц. «в лице» — нет chip-segment физ/юр per PRD)
  customerName: z.string().max(500).default(''),
  /** Опционально, для юр.лиц. */
  customerRepName: z.string().max(500).default(''),
  customerRepBasis: z.string().max(500).default(''),
  executorName: z.string().max(500).default(''),
  executorRepName: z.string().max(500).default(''),
  executorRepBasis: z.string().max(500).default(''),

  // Стоимость и сроки
  costSource: z.enum(CONTRACT_COST_SOURCES).default('manual'),
  /**
   * Phase 6.5 Amendment A1 (2026-05-24) — currency user-controlled, не
   * template-derived. Default = `CONTRACT_CURRENCY_PER_REGION[region]` set в
   * `createInitialContractDraft()`. User может override через chip-segment
   * в SectionCostAndTerms (UI ships в 6.5C). Используется renderContractToPdf
   * + UI labels (через `getCurrencyShort` / `getCurrencyWord` helpers).
   */
  currencyCode: z.enum(CONTRACT_CURRENCY_CODES).default('KZT'),
  totalCost: z.number().int().min(0).max(100_000_000_000).default(0),
  advance: z.number().int().min(0).max(100_000_000_000).default(0),
  /**
   * PR-D2 (ADR-0040, Zod-approved Aibek 2026-05-31) — хранимая ставка «Аванс, %»
   * (0–100). Когда задана валидным числом → сумма аванса = производное
   * `resolveAdvanceAmount(selectTotalSpec, advancePercent, advance)` =
   * `round(Сумма × % / 100)`, пересчитывается при изменении Суммы; поле `advance`
   * остаётся ручным fallback (read-only в UI при %-режиме). `null`/`undefined`
   * (legacy-договоры) → ручной режим по `advance`. IDB DB_VERSION НЕ бампится
   * (optional/nullable, structured-clone backward-compat — как `seededFromQuoteAt`).
   */
  advancePercent: z.number().min(0).max(100).nullable().optional(),
  // remaining = selectTotalSpec - resolveAdvanceAmount(...) (derived, NOT в state)
  durationDays: z.number().int().min(1).max(365).default(30),
  warrantyTerm: z.number().int().min(1).max(120).default(24),
  warrantyUnit: z.enum(CONTRACT_WARRANTY_UNITS).default('months'),
  /**
   * Phase 6.5 Amendment A3 (2026-05-24) — custom rows в Cost section (#7).
   * Свободные дополнительные строки like «Срок гарантии (нестандартный)».
   * Cap 20 — practical UX limit (больше = заполнение становится спецификацией).
   */
  costCustomRows: z.array(ContractCostCustomRowSchema).max(20).default([]),

  // Спецификация (Приложение №1)
  spec: z.array(ContractSpecBlockSchema).max(50).default([]),

  // Реквизиты — анкета (P-009: customer's transient)
  customerRequisites: z.array(ContractRequisiteSchema).max(50).default([]),
  executorRequisites: z.array(ContractRequisiteSchema).max(50).default([]),

  // Комментарии (свободный текст)
  comments: z.string().max(5000).default(''),

  /**
   * Phase 7 hotfix round 4 (ADR-0025 Amendment A9, 2026-05-26) — opt-in toggle
   * для preservation customer values после PDF Download/Share. Default OFF
   * (auto-clear behaviour из Phase 7.1 preserved для compliance — 152-ФЗ /
   * Закон РК о ПДн / Закон РУз о ПДн minimization). ON = мебельщик-«серийник»
   * хочет сохранить customer data для повторного использования (10+ договоров
   * одному ритейл-клиенту в месяц).
   *
   * Юридический контекст: явный opt-in от мебельщика как оператора ПДн
   * клиента = информированное согласие per 152-ФЗ ст. 6 / аналогов в КЗ/УЗ.
   *
   * Existing drafts без поля → Zod default false → auto-clear preserved
   * (no regression). IDB DB_VERSION НЕ trogаем.
   */
  preserveCustomerRequisites: z.boolean().default(false),

  // Source (Block 12 integration)
  /** UUID сохранённого КП-источника (если договор создан из КП). null для standalone. */
  fromQuoteId: z.string().uuid().nullable().default(null),

  /**
   * PR-C2 Phase 2 (ADR-0036 amends ADR-0025, 2026-05-30) — частичный пересев
   * Договора из изменённого КП. Зеркало ADR-0027 (пересев КП от анкеты).
   *
   * Три optional поля (без `.default()` — `undefined` означает legacy/standalone
   * договор, для которого invalidation skip'ается). Заполняются при первом севе
   * (`mapQuoteStateToContractDraft` → `useFromQuoteSeeding` HYDRATE) и при каждом
   * `PARTIAL_RESEED_CONTRACT`. IDB DB_VERSION НЕ trogается (structured-clone,
   * backward-compat без миграции — как ADR-0027).
   *
   *   - `seededFromQuoteAt` — Unix ms последнего сева/пересева из КП.
   *   - `lastQuoteUpdatedAt` — `quote.updatedAt` на момент последнего сева.
   *     Триггер invalidation: `quote.updatedAt > lastQuoteUpdatedAt` → пересев.
   *   - `seededSpecHash` — канонический хеш КП-производной спецификации на момент
   *     сева (механизм B, ADR-0036 §2a). Pristine-детектор: совпал с текущим
   *     `computeSpecHash(spec)` → пользователь не трогал спец → можно пересеять
   *     целиком; не совпал → есть ручные правки → спец не трогаем (решение #4),
   *     обновляем только cost при costSource='fromQuote' (решение #3).
   */
  seededFromQuoteAt: z.number().int().nonnegative().optional(),
  lastQuoteUpdatedAt: z.number().int().nonnegative().optional(),
  seededSpecHash: z.string().max(64).optional(),

  /**
   * PR-C5 (ADR-0039, 2026-05-31, Вариант 2) — `quote.updatedAt`, для которого
   * пользователь последний раз **ответил** на промпт «Обновить данные из КП?»
   * (Да ИЛИ Нет). Драйвит подавление модалки: показывается ⟺
   * `quote.updatedAt > max(lastQuoteUpdatedAt, quotePromptAckAt)`. Бампится при
   * «Да» (вместе с `lastQuoteUpdatedAt`) и при «Нет» (только это поле — чтобы
   * ссылка «↻ Обновить из КП» осталась видна между визитами, пока КП новее
   * последнего реального сева). Optional/legacy → undefined (модалка по
   * `lastQuoteUpdatedAt`). IDB version НЕ бампится (structured-clone backward-compat).
   */
  quotePromptAckAt: z.number().int().nonnegative().optional(),

  // Timestamps (Unix ms)
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
});
export type ContractDraft = z.infer<typeof ContractDraftSchema>;

/** Sanity-check: QUOTE_FURNITURE_TYPES не отсюда экспортируется, но используется
 * UI для предложения дефолтных titles блоков спецификации. Re-export не нужен
 * (callers импортируют напрямую из quote-furniture-types-vocabulary).
 */
export const CONTRACT_DEFAULT_SPEC_TITLES = QUOTE_FURNITURE_TYPES;
